import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppInitService } from 'src/app/app-init.service';
import { AppConfigurations } from 'src/app/shared/models/appConfiguration.model';
import * as CoreActions from '../../state/actions/core.actions';
import * as MessageHubActions from '../../state/actions/message-hub.actions';
import { MessageHubActionType } from "../enums/messageHubActionType.enum";
import { MessageHubEvent } from '../enums/messageHubEvent.enum';
import { MessageHubGroup } from '../enums/messageHubGroup.enum';
import { AlertUserGroup } from '../models/message-hub/alertUserGroupRequest';
import { AppointmentGridGroup } from '../models/message-hub/appointmentGridGroupRequest';
import { CentralAppointmentGridGroup } from '../models/message-hub/centralAppointmentGridGroup';
import { CentralBranchGridGroup } from '../models/message-hub/centralBranchGridGroupRequest';
import { CentralLobbyEntryGridGroup } from '../models/message-hub/centralLobbyEntryGridGroupRequest';
import { CentralStaffStatusGridGroup } from '../models/message-hub/centralStaffStatusGridGroupRequest';
import { ClientCreateGroup } from '../models/message-hub/clientCreateGroupRequest';
import { DataImportGroup } from '../models/message-hub/dataImportGroup';
import { LobbyEntryGridGroup } from '../models/message-hub/lobbyEntryGridGroupRequest';
import { LocationSelectorStatusGroup } from '../models/message-hub/locationSelectorStatusRequest';
import { SignInUsersGroup } from '../models/message-hub/signInUsersGroupRequest';
import { StaffStatusGridGroup } from '../models/message-hub/staffStatusGridGroupRequest';
import { StatBarGroup } from '../models/message-hub/statBarGroupRequest';
import { SyncGroup } from '../models/message-hub/syncGroupRequest';
import { SystemConfigurationGroup } from '../models/message-hub/systemConfigurationRequest';
import { UserStatusGroup } from '../models/message-hub/userStatusGroupRequest';
import { LobbyEntryWaitCountByLocationGroup } from './../models/message-hub/LobbyEntryWaitCountByLocationGroupRequest';
import { GlobalDataService } from './global-data.service';
import { DomSanitizer } from '@angular/platform-browser';
import { AppointmentDashboardGroup } from '../models/message-hub/appointmentDashboardGroup';
import { AppointmentStaffDashboardGroup } from '../models/message-hub/appointmentStaffDashboardGroup';
@Injectable({
  providedIn: 'root'
})
export class MessageHubService {

  private hubConnection: HubConnection | undefined;
  isAuthorizedSubscription: Subscription | undefined;
  isAuthorized = false;
  appConfig!: AppConfigurations;

  constructor(private store: Store<any>, private appInitService: AppInitService,
    private globalDataService: GlobalDataService) {
    appInitService.getAppConfiguration$().subscribe({
      next: (config) => this._onReceiveAppConfig(config)
    })
  }

  private _onReceiveAppConfig(appConfig: AppConfigurations) {
    this.appConfig = appConfig;
    this.initMessageHub();
  }

  public subscribeToAlertUserGroup(request: AlertUserGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.AlertUserGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToAppointmentGridGroup(request: AppointmentGridGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.AppointmentGridGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToCentralAppointmentGridGroup(request: CentralAppointmentGridGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.CentralAppointmentGridGroup,
        JSON.stringify(request));
    }
  }

  //Subscription for appointment status by location widget
  public subscribeToAppointmentDashboardGroup(request: AppointmentDashboardGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke('SubscribeToGroup', 'AppointmentStatusCountByLocationGroup', JSON.stringify(request));
    }
  }

  //Subscription for staff appointment widget
  public subscribeToAppointmentStaffDashboardGroup(request: AppointmentStaffDashboardGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke('SubscribeToGroup', 'StaffAppointmentsGroup', JSON.stringify(request));
    }
  }

  // Client Method: LoadSignalRCentralBranchStatusGrid
  public subscribeToCentralBranchGridGroup(request: CentralBranchGridGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.CentralBranchGridGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToCentralLobbyEntryGridGroup(request: CentralLobbyEntryGridGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.CentralLobbyEntryGridGroup,
        JSON.stringify(request));
    }
  }

  // Client Method: LoadSignalRCentralStaffStatusGrid
  public subscribeToCentralStaffStatusGridGroup(request: CentralStaffStatusGridGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.CentralStaffStatusGridGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToLobbyEntryGridGroup(request: LobbyEntryGridGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.LobbyEntryGridGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToSignInUsersGroup(request: SignInUsersGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.SignInUsersGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToStaffStatusGridGroup(request: StaffStatusGridGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.StaffStatusGridGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToStatBarGroup(request: StatBarGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.StatBarGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToUserStatusGroup(request: UserStatusGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.UserStatusGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToSystemConfigurationGroup(request: SystemConfigurationGroup) {
    if (this.hubConnection && this.hubConnection.state == signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.SystemConfigurationGroup,
        JSON.stringify(request));
    } else {
      this.store.dispatch(CoreActions.loadSystemConfigurationsApi({
        payload: {
          locationId: request.LocationId,
          tenantId: request.TenantId,
          userId: request.UserId
        }
      }));
    }
  }

  public subscribeToLocationSelectorStatusGroup(request: LocationSelectorStatusGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.LocationSelectorStatusGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToDataImportGroup(request: DataImportGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.DataImportGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToLobbyEntryWaitCountByLocationGroup(request: LobbyEntryWaitCountByLocationGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.LobbyEntryWaitCountByLocationGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToAppointmentCountByLocationGroup(request: LobbyEntryWaitCountByLocationGroup) {
    if (this.hubConnection) {
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.AppointmentWaitCountByLocationGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToSyncGroup(request: SyncGroup) {
    if (this.hubConnection) {
      console.debug(`Subscribing to syncGroupWith: ${request.TenantId}:${request.UserId}`)
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.SyncGroup,
        JSON.stringify(request));
    }
  }

  public subscribeToClientCreateGroup(request: ClientCreateGroup) {
    if (this.hubConnection) {
      console.debug(`Subscribing to clientCreateGroupWith: ${request.CreatedExternalUserId}`)
      this.hubConnection.invoke(MessageHubActionType.SubscribeToGroup,
        MessageHubGroup.ClientCreateGroup,
        JSON.stringify(request));
    }
  }

  public unSubscribeFromGroups(groupNames: string[]) {
    groupNames?.forEach(groupName => {
      if (this.hubConnection) {
        this.hubConnection.invoke(MessageHubActionType.UnSubscribeFromGroup, groupName);
      }
    });
  }

  public initMessageHub() {
    const token = this.globalDataService.getAccessToken();
    let bearerValue = '';
    if (token !== '') {
      bearerValue = `?token=Bearer ${token}`;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.appConfig.messageHub.baseApiUrl}${bearerValue}`)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.hubConnection
      .start()
      .then(() => {
        this.store.dispatch(MessageHubActions.messageHubConnectionStarted());
      })
      .catch(err =>
        console.error(err.toString())
      );
    this.hubConnection.onreconnected(() => {
      this.store.dispatch(MessageHubActions.messageHubConnectionReStarted());
    })

    this.hubConnection.on(MessageHubEvent.GetActiveAlertsForUserComplete,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedActiveAlertsForUser({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRAppointmentGrid,
      (messageData: string) => {
        if (messageData) {
          const dataObj = JSON.parse(messageData);
          dataObj.data.forEach((element: any) => {
            element.appointmentServiceRequests = (element.appointmentServiceRequests !== null &&
              element.appointmentServiceRequests !== '' &&
              element.appointmentServiceRequests !== undefined) ?
              element.appointmentServiceRequests.split(',') : [];
            element.appointmentServiceRequestsIds = (element.appointmentServiceRequestsIds !== null &&
              element.appointmentServiceRequestsIds !== '' &&
              element.appointmentServiceRequestsIds !== undefined) ?
              element.appointmentServiceRequestsIds.split(',').map((x: string | number) => +x) : [];
          });

          messageData = JSON.stringify(dataObj);
        }
        this.store.dispatch(MessageHubActions.receivedAppointmentGridData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRMyAppointmentGrid,
      (messageData: string) => {
        if (messageData) {
          const dataObj = JSON.parse(messageData);
          dataObj.data.forEach((element: any) => {
            element.appointmentServiceRequests = (element.appointmentServiceRequests !== null &&
              element.appointmentServiceRequests !== '' &&
              element.appointmentServiceRequests !== undefined) ?
              element.appointmentServiceRequests.split(',') : [];

            element.appointmentServiceRequestsIds = (element.appointmentServiceRequestsIds !== null &&
              element.appointmentServiceRequestsIds !== '' &&
              element.appointmentServiceRequestsIds !== undefined) ?
              element.appointmentServiceRequestsIds.split(',').map((x: string | number) => +x) : [];
          });

          messageData = JSON.stringify(dataObj);
        }
        this.store.dispatch(MessageHubActions.receivedMyAppointmentGridData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRCentralBranchStatusGrid,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedCentralBranchStatusGridData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRCentralLobbyStatusGrid,
      (messageData: string) => {

        if (messageData) {
          const dataObj = JSON.parse(messageData);
          dataObj.data.forEach((element: any) => {
            element.serviceRequested = (element.serviceRequested !== null) ? element.serviceRequested.split(',') : [];
          });

          messageData = JSON.stringify(dataObj);
        }
        this.store.dispatch(MessageHubActions.receivedCentralLobbyStatusGridData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRCentralStaffStatusGrid,
      (messageData: string) => {
        if (messageData) {
          const dataObj = JSON.parse(messageData);
          dataObj.data.forEach((element: any) => {
            element.serviceRequested = (element.serviceRequested !== null) ? element.serviceRequested.split(',') : [];
          });

          messageData = JSON.stringify(dataObj);
        }
        this.store.dispatch(MessageHubActions.receivedCentralStaffStatusGridData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRLobbyStatusGrid,
      (messageData: string) => {
        if (messageData) {
          const dataObj = JSON.parse(messageData);
          dataObj.data.forEach((element: any) => {
            element.serviceRequested = (element.serviceRequested !== null) ? element.serviceRequested.split(',') : [];
          });

          messageData = JSON.stringify(dataObj);
        }
        this.store.dispatch(MessageHubActions.receivedLobbyStatusGridData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.GetSignedInUsersAndGroups,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedSignedInUsersAndGroupsData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRStaffStatusGrid,
      (messageData: string) => {
        if (messageData) {
          const dataObj = JSON.parse(messageData);
          dataObj.data.forEach((element: any) => {
            element.serviceRequested = (element.serviceRequested !== null) ? element.serviceRequested.split(',') : [];
          });

          messageData = JSON.stringify(dataObj);
        }
        this.store.dispatch(MessageHubActions.receivedSignalRStaffStatusGridData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.GetStatBarDataComplete,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedStatBarData({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.GetUserSignedInStatus,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedUserSignedInStatus({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRLocationSelectorStatus,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedLobbyLocationSelectorStatus({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.LoadSignalRSystemConfiguration,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedSystemConfigurationData({
          payload: {
            systemConfigurations: JSON.parse(messageData)
          }
        }));
      });

    this.hubConnection.on(MessageHubEvent.ProvisionLogDisplay,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedProvisionLogData({ messageData }));
      })

    this.hubConnection.on(MessageHubEvent.LoadLobbyEntryWaitCountByLocation,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedLobbyEntryWaitCountByLocation({ messageData }));
      });

    this.hubConnection.on(MessageHubEvent.AppointmentEntryWaitCountByLocation,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedAppointmentWaitCountByLocation({ messageData }));
      });
    this.hubConnection.on('AppointmentStatusByLocation', (messageData: any) => {
      this.store.dispatch(MessageHubActions.receivedAppointmentStatusByLocationData({ messageData }));
    });
    this.hubConnection.on('AppointmentsByStaff', (messageData: any) => {
      this.store.dispatch(MessageHubActions.receivedStaffAppointmentData({ messageData }));
    });
      this.hubConnection.on(MessageHubEvent.SyncStatus,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedSyncStatus({ messageData }));
      })
    this.hubConnection.on(MessageHubEvent.ClientCreateStatus,
      (messageData: string) => {
        this.store.dispatch(MessageHubActions.receivedClientCreateStatus({ messageData }));
      });
  }

}
